import { AutocompleteChangeReason } from "@mui/material";
import { ETableEditAction, EWidths } from "./Enums";
import {
  AccountModel,
  AlertModel,
  Attachment,
  ClassModel,
  ClientModel,
  CompanyModel,
  ContactModel,
  ConversationModel,
  CourseModel,
  DocumentsModel,
  EmailTemplateModel,
  ExamQuestionModel,
  InstructorModel,
  LogModel,
  MessageModel,
  NoteModel,
  OblioBillingDocument,
  OrderModel,
  ProductsModel,
  SubscriptionModel,
  TaskStatusModel,
  UserModel,
  VehicleModel,
  WhatsAppBusinessAccountData,
  WorkPointModel,
  ExamsSetModel,
} from "./Models";
import { ReactNode } from "react";
import {
  Control,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { OrderValidationSchema } from "../Utils/Models";

export type Role = {
  name: string;
  priority: string | number;
  createdAt: string;
  updatedAt: string;
  _id: string;
};

export type Address = {
  city: string;
  county: string;
  country: string;
  details: string;
  _id: string;
};

export type Response = {
  status: string | number;
  data: any;
};

export type Notification = {
  id: string;
  title: string;
  message: string;
  type: "info" | "success" | "warning" | "error";
  timestamp: Date;
  read: boolean;
  data?: any;
};

export type NotificationConfiguration = {
  _id?: string;
  notificationType: string;
  sendEmail: boolean;
  sendNotification: boolean;
  sendSMS: boolean;
  roles: string[]; // Array of role IDs or names
  selectedUsers: string[]; // Array of user IDs, empty means all users
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type UserStateProps = {
  _id: string | number;
  firstName: string | number;
  lastName: string | number;
  email: string | number;
  username: string | number;
  roleIds: [Role];
};

export type PaginatedResponse = {
  status: string | number;
  data: {
    results: any[];
    numberOfResults: number;
  };
};

export type TodaysContactsResponse = {
  status: string | number;
  data: {
    noTotalResults: number;
    noTakenContacts: number;
    noAvailableContacts: number;
    noDelayedContacts: number;
  };
};

export type ContactsResumeProps = {
  noTotalResults: number | null;
  noTakenContacts: number | null;
  noAvailableContacts: number | null;
};

export type CustomTabPanelProps = {
  children: React.ReactNode;
  value: any;
  index: any;
};

export type ProductPanelProps = {
  index?: any;
  product?: any;
  reset?: any;
  register?: any;
  control?: any;
  getValues?: any;
  setValue?: any;
  watch?: any;
  disabled?: boolean;
  invoiceGeneration?: boolean;
  products?: any;
  instructors?: InstructorModel[];
  vehicles?: VehicleModel[];
  managements?: any;
  trigger?: any;
  clearErrors?: any;
  restrictByWorkPoint?: boolean;
  name?: string;
};

export type ProductPanelOrderOfferProps = {
  index?: any;
  product?: any;
  reset?: any;
  register?: any;
  control?: any;
  getValues?: any;
  setValue?: any;
  watch?: any;
  disabled?: boolean;
  products: any;
  isOrderOffer?: boolean;
};

export type TransportMethodsProps = {
  control?: any;
  getValues?: any;
  setValue?: any;
  disabled?: boolean;
  transportOptions: any;
  error?: boolean;
};

export type ClientPanelProps = {
  reset?: any;
  register?: any;
  getValues?: any;
  setValue?: any;
  control?: any;
  formState?: any;
  watch?: any;
  clearErrors?: any;
  disabled?: boolean;
  saveOrder: any;
  products: any;
  instructors: any[];
  allDocumentTypes: any[];
  trigger?: any;
  collectPaymentType?: string | string[];
  triggerReload: any;
  setTriggerReload: any;
  showCollectPaymentButton?: boolean;
  showEditClientButton?: boolean;
  restrictAccess?: boolean;
  editPrice?: boolean;
  editDrivingHours?: boolean;
  clientDocuments?: any[];
  onDocumentSaved?: () => void;
  hasReadiness?: boolean;
  readinessProgress?: number;
  clientReadinessData?: any;
};

export type ClientPanelOrderOfferProps = {
  reset?: any;
  register?: any;
  getValues?: any;
  setValue?: any;
  control?: any;
  formState?: any;
  watch?: any;
  clearErrors?: any;
};

export type EditProductProps = {
  searchUrl?: string;
  onProductChange: (
    e: any,
    value: ProductsModel | null,
    reason: AutocompleteChangeReason,
    details?: any
  ) => void;
  disabled?: boolean;
  control?: any;
  getValues?: any;
  index?: any;
  setValue?: any;
  watch?: any;
  products?: ProductsModel[];
  instructors?: InstructorModel[];
  vehicles?: VehicleModel[];
  isOrderOffer?: boolean;
  productType?: string | null | undefined;
  applyStyle?: boolean;
  id?: string;
};

export type PaymentPanelProps = {
  getValues?: any;
  setValue?: any;
  disabled?: boolean;
  error?: boolean;
  paymentMethods: any[];
  control?: any;
  watch?: any;
};

export type TotalPanelProps = {
  reset?: any;
  register?: any;
  control?: any;
  getValues?: any;
  setValue?: any;
  watch?: any;
  disabled?: boolean;
  isOrderOffer?: boolean;
};

export type ShippingPanelProps = {
  reset?: any;
  register?: any;
  getValues?: any;
  setValue?: any;
  formState?: any;
  watch?: any;
  disabled?: boolean;
  error?: boolean;
};

export type DeleteActionProps = {
  onCancelAction: () => void;
  onDeleteAction: () => void;
};

export type DropzoneComponentProps = {
  onSave: (file: any) => void;
  paths?: any[];
  acceptedFileTypes?: any;
  error?: any;
  altText?: string;
  maxFiles?: number;
  multiple?: boolean;
  maxSize?: number;
  isPreview?: boolean;
};

export type SpecialDividerProps = {
  title?: string;
  button?: any;
};

export type DynamicTabsProps = {
  labels: string[] | Label[];
  tabValue: number;
  handleChange?: (tabIndex: number) => void;
  badge?: any;
  key?: any;
};

export type FileMessageExplorerProps = {
  message: MessageModel;
};

export type Label = {
  label: string;
  badge: number | string;
  tabColor?: string;
};

export type LoginResponseType = {
  status: string | number;
  data: {
    userId: string;
    userName: string;
    access_token: string;
    roles: any[];
    fullName: string;
    workPointId: string;
    bankAccountId: string;
    canScheduleHours?: boolean;
  };
};

export type ContactWidgetProps = {
  contact: ContactModel;
  key: number;
};

export type StepperNotesProps = {
  notes: Array<any>;
  updateNotes: (notes: Array<any>) => void;
  disabled?: boolean;
};

export type DisplayOneNoteProps = {
  note: any;
  handleEdit: (note: any) => void;
  handleDelete: (note: any) => void;
  disabled?: boolean;
};

export type ContactSummaryProps = {
  onClick: () => void;
  contact: ContactModel;
};

export type CustomDrawerProps = {
  open: boolean;
  handleDrawerClose: () => void;
  title: string;
  children: any;
  formId: string;
  okText?: string;
  cancelText?: string;
  className?: string;
};

export type EditNoteProps = {
  note: NoteModel;
  open: boolean;
  handleClose: () => void;
  setContextValue: (note: any) => void;
  setShouldSubmit: (value: boolean) => void;
  id?: string | null;
};

export type CreateAndUpdateOrderResponse = {
  status: string | number;
  data: OrderModel;
};

export type ContactOrderProps = {
  setContextValue: (contact: ContactModel) => void;
  disabled: boolean;
};

export type GlobalContextType = {
  contactId: string | null;
  setContactId: (value: string | null) => void;
};

export type ContactPhoneProps = {
  handleChange: (property: string, value: string) => void;
  disabled: boolean;
};

export type CustomInfiniteScrollListProps = {
  id?: string | number;
  items: any[];
  render: (item: any, index: number) => JSX.Element;
  listStyle?: any;
  listItemsStyle?: any;
  style?: any;
};

export type ContactModalProps = {
  contactId: string | null | undefined;
  setShouldUpdate: (value: boolean) => void;
  open: boolean;
  setOpen: (value: boolean) => void;
};

export type CustomPanelProps = {
  title: string;
  children: React.ReactNode;
  actionButtons?: any;
  error?: any;
  style?: any;
};

export type OrderOfferCustomPanelProps = {
  children: React.ReactNode;
  title: string;
  error?: boolean;
};

export type NoDataFoundProps = {
  classNames?: any;
  text?: string;
};

export type MainSliderProps = {
  name: string;
  data: any;
  setHideSlider?: any;
};

export type ImportExcelModalProps = {
  headers: string[];
  title: string;
  setLoading?: (loading: boolean) => void;
  openImportModal: boolean;
  setOpenImportModal: (openImportModal: boolean) => void;
  getNewRow: (row: any) => void;
  getRowsAsArrayData: (data: any) => void;
};

export type GenericModalProps = {
  open: boolean;
  onClose: () => void;
  children: any;
  title: string;
  actions?: any;
  style?: any;
  className?: string;
  maxWidth?: EWidths | false;
  width?: string;
  otherOptions?: any;
};

export type MUIDataTableCustomStyleProps = {
  title: string;
  columns: any;
  onEditAction:
    | ETableEditAction.MODAL
    | ETableEditAction.REDIRECT
    | ETableEditAction.INLINE
    | ETableEditAction.NONE;
  onAddAction?:
    | ETableEditAction.MODAL
    | ETableEditAction.REDIRECT
    | ETableEditAction.INLINE
    | ETableEditAction.NONE;
  id?: string | "";
  hideAddButton?: boolean;
  hideOptions?: boolean;
  options?: any;
  buttons?: any | JSX.Element;
  CRUDEndPoint?: string;
  addOrEditForm?: any; //JSX.Element
  setContextValue?: any;
  CRUDContext?: any;
  reloadURL?: any;
  triggerReload?: boolean;
  customTriggerReload?: boolean;
  rowClickURL?: string;
  addURL?: string;
  editURL?: string;
  onRowClick?: (rowData: any) => void;
  handleModalForm?: any;
  handleInlineForm?: any;
  hideDeleteButton?: boolean;
  hideEditButton?: boolean;
  hideSearch?: boolean;
  hideToolbar?: boolean;
  renderCustomColumns?: (rowIndex: number, value: any) => any;
  selectableRows?: any;
  filters?: any;
  addEntityIdToURL?: boolean;
  rowProps?: any;
  customToolbarSelect?: (
    selectedRows: any,
    displayData: any,
    setSelectedRows: any
  ) => any;
  onRowSelectionChange?: (selectedRows: any, selectedRowData: any[]) => void;
  shouldTriggerStorageUpdate?: boolean;
  customRowRender?: (
    row: any,
    dataIndex: number,
    rowIndex: number,
    tableData: any[]
  ) => any;
  viewColumns?: boolean;
  setTriggerReload?: () => void;
  customFooter?: (tableData: any) => any;
  columnsWithCustomSort?: string[];

  // This needs to be put in another object and the object to bge optional but the props need to be required
  expandableRows?: boolean; // will have expandable rows
  expandableRowsHeader?: boolean; // has the table a header
  expandableRowObjectLocationName?: string; // from what object do we get the data
  expandableColumns?: any; // the header of the expandable row
  onExpandableRowClick?: (rowData: any) => void;
  expandableSubRowsKey?: string; // key inside each expandable item that holds sub-rows
  expandableSubColumns?: any; // columns for sub-rows
  onExpandableSubRowClick?: (rowData: any) => void;
  customOptions?: any;

  // Print and Download options
  showPrintButton?: boolean;
  showDownloadButton?: boolean;
  exportTitle?: string; // Title for print/download - will include date range if available
  exportFooterFields?: (footerFields: any) => { label: string; value: string | number; color?: string }[];
  exportWithTimestamp?: boolean; // Add timestamp to exported file name
  rowDeleteValidation?: (rowData: any) => boolean; // Function to validate if delete button should be enabled for a row
};

export type StandardActionButtonsProps = {
  okAction?: () => void;
  cancelAction: () => void;
  okText: string;
  cancelText: string;
  formId?: string;
  type?: "submit" | "button";
};

export type EmailTemplateImagesProps = {
  handleUploadAttachment: (file: any, alt: string) => void;
  id?: any;
};

export type EmailTemplateDeleteImagesProps = {
  emailTemplate: EmailTemplateModel;
  handleDeleteImage: (attachment: Attachment) => void;
  id?: any;
};

export type EditorToolbarCustomButtonProps = {
  children: any;
};

export type ListItemBillingDocProps = {
  doc: OblioBillingDocument;
  handleDeleteBillingDoc: (
    series: string,
    number: string,
    type: string
  ) => void;
  handleCancelBillingDoc: (
    series: string,
    number: string,
    type: string
  ) => void;
  handleIssueInvoiceOnNotice: (doc: OblioBillingDocument) => void;
  handleIssueCollectInvoice: (doc: OblioBillingDocument) => void;
};

export type ListItemProductLineProps = {
  name: string;
  vatPercentage?: string;
  quantity: string;
  priceWithoutTVA: string;
  price: string;
  management: string;
  discount?: string;
  index?: number;
  control?: any;
  managements?: any;
  productType?: string;
};

export type InvoicePanelProps = {
  handleSaveOrder: (order: OrderModel) => void;
  getOrderValues?: any;
  watchOrder?: any;
  managements: any[];
};

export type MainHeaderProps = {
  setHideSlider: () => void;
  name: string;
  shouldRenderSearchBar?: boolean;
  data?: any;
};

export type DashboardPageProps = {
  shouldRenderSearchBar?: boolean;
};

export type PageNotFoundProps = {
  text: string;
};

export type EditClientModalProps = {
  onClientChange: (
    e: any,
    value: ClientModel | null,
    reason: AutocompleteChangeReason,
    details?: any
  ) => void;
  disabled: boolean;
  control?: any;
  getValues?: any;
  formState?: any;
  setValue?: any;
  clearErrors?: any;
  open: boolean;
  setOpen: (open: boolean) => void;
};

export type PeriodicityProps = {
  periodicityLength: number;
  control: any;
  client: any;
  getValues: any;
  setValue: any;
  renderLess?: boolean;
};

export type AssociateAgentWithContactModalProps = {
  contact: any;
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
};

export type ContactsListProps = {
  shouldUpdate: boolean;
  setShouldUpdate: any;
  url: string;
  websocketEventName: string;
};

export type WYSIWYGEditorProps = {
  value: any;
  toolbar: any;
  mention?: any;
  hashtag?: any;
  onChange?: (value: any) => void;
  readOnly?: boolean;
  formRef?: any;
  reset?: boolean;
  classOverride?: any;
  id?: string;
  className?: string;
  allowNewLine?: boolean;
};

export type ClientFormProps = {
  selectedClient?: ClientModel | any;
  onClose?: any;
  handleContentChange?: () => void;
  onSuccess?: any;
};

export type BillingDocClientDataProps = {
  register: any;
  formState: any;
  clientData?: any;
};

export type TabPanelProps = {
  children: ReactNode;
  value: number;
  index: number;
  style?: any;
  className?: string;
};

export type StatusPanelType = {
  selectedOption?: object | any;
  handleChange?: void | any;
  allOptions?: object[] | any;
  disabled?: boolean;
  getValues?: any;
  control?: any;
  reset?: any;
  setTriggerReload: (value: boolean) => void;
  triggerReload: boolean | undefined;
  excludedStatuses?: string[];
};

export type AddOrderStatusModalProps = {
  openAddOrderStatusModal: boolean;
  handleChangeOrderStatusModalValue: () => void;
  selectedRow: any;
  sortNumber: number;
  getOrderStatus: () => void;
};

export type DeleteOrderStatusProps = {
  openDeleteModal: boolean;
  setOpenDeleteModal: (openDeleteModal: boolean) => void;
  handleDelete: () => void;
};

export type ImageGalleryProps = {
  images: any[];
};

export type AdditionalDataProps = {
  openMenu: boolean;
  anchorEl: any;
  handleClose: (tag: any) => void;
  handleClick: (event: any) => void;
};

export type EmailTemplateFormProps = {
  onClose?: () => void;
};

export type NotificationConfigurationFormProps = {
  selectedRow?: NotificationConfiguration | any;
  onClose: () => void;
  notificationTypes: any[];
  users: any[];
};

export type UserFormProps = {
  selectedUser?: UserModel | any;
  setContextValue?: any;
  onClose?: any;
};

export type WorkPointFormProps = {
  selectedWorkPoint?: WorkPointModel | any;
  setContextValue?: any;
  onClose?: any;
  companies?: CompanyModel[];
};

export type CompanyFormProps = {
  selectedCompany?: CompanyModel | any;
  setContextValue?: any;
  onClose?: any;
};

export type VehicleFormProps = {
  selectedVehicle?: VehicleModel | any;
  setContextValue?: any;
  onClose?: any;
  restrictByWorkPoint?: boolean;
};

export type InstructorFormProps = {
  selectedInstructor?: InstructorModel | any;
  setContextValue?: any;
  onClose?: any;
};

export type ProductFormProps = {
  selectedProduct?: ProductsModel | any;
  setContextValue?: any;
  onClose?: any;
  examCategories?: string[]; // Array of exam categories
};

export type DocumentsFormProps = {
  selectedDocument?: DocumentsModel | any;
  setDocumentsContextValue?: any;
  onClose?: any;
};

export type LocalRouterProps = {
  routes: any;
  name: string;
};

export type ConnectWithFacebookProps = {
  setShouldUpdate: any;
};

export type WhatsAppMenuProps = {
  whatsAppBusinessAccountData: WhatsAppBusinessAccountData;
};

export type WhatsAppTemplateComponentContainerProps = {
  children: ReactNode;
  name: string;
  isOptional?: boolean;
  otherHeaderComponents?: ReactNode;
};

export type TemplateHeaderComponentProps = {
  methods: any;
};

export type TemplateBodyComponentProps = {
  methods: any;
};

export type ObservationWidgetProps = {
  text: string;
  shouldRenderIcon?: boolean;
  link?: string;
};

export type TemplateButtonComponentProps = {
  methods: any;
};

export type WhatsAppTagManagementComponentProps = {
  methods: any;
  children: ReactNode;
  examplePropertyName: string;
  mainProperty: string;
  maxNrOfTags?: number;
  maxlengthMainString?: number;
};

export type HelperTextProps = {
  errorText: string | undefined;
  textLength: number | undefined;
  maxLength: number | undefined;
};

export type PhoneRegistrationStepType = {
  label: string;
  component: ReactNode;
  formName: string;
};

export type ChatProps = {
  className?: string | null | undefined;
  id?: string | null;
  disabled?: boolean;
};

export type MessagesListProps = {
  messages: MessageModel[];
  shouldScrollPage: boolean;
  replyToMessage: (message: MessageModel) => void;
  renderMessageContext: (messageId: string | null | undefined) => ReactNode;
  disabled?: boolean;
  key: string;
};

export type ChatTemplatesProps = {
  templates: any[];
  clientId: string | null;
  sendTemplateMessage: (message: any) => void;
  disabled?: boolean;
};

export type WhatsAppTemplatePreviewProps = {
  template: any;
  shouldRenderCurrentDate?: boolean;
};

export type LogProps = {
  log: LogModel;
};

export type FiltersProps = {
  filters: Filter[];
  customFilterModel?: object;
  defaultOpen?: boolean;
  filterWidth?: number;
};

export type Filter = {
  property: string;
  label: string;
  type: string;
  validation: any;
  defaultValue: any;
  options?: any;
  autocompleteFilterProperty?: string;
  disableFlag?: boolean;
  controlProperty?: string;
  otherOptions?: any;
  customGetOptionLabel?: (option: any) => string;
  clearable?: boolean;
};

export type DirtyFormGuardProps = {
  when: any;
};

export type PreviewDocumentProps = {
  open: boolean;
  title: string;
  onClose: (emailSent: boolean) => void;
  htmlContent: string;
  url?: string;
  customTopMenu?: JSX.Element | null | undefined;
  hideDownloadButton?: boolean;
  hidePrintButton?: boolean;
};

export type FilePreviewProps = {
  file: any;
  setFile: (file: any) => void;
  phone: string;
  conversationId: string | null | undefined;
  className?: string | null | undefined;
};

export type MessagesAttachmentsProps = {
  setFile: (file: any) => void;
};

export type ChatOptionsProps = {
  chat: ConversationModel;
  handleDeleteConversation: () => void;
  updateConversationState: (conversation: ConversationModel) => void;
};

export type ClientsAutocompleteProps = {
  formId: string;
  submitAction: (data: any) => void;
};

export type SearchComponentProps = {
  searchParamName?: string;
};

export type OptionSelectorProps = {
  questionText: string;
  selectedOption: string;
  options: string[]; // Array of options
  valueRadio: string;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    selectedValue: string
  ) => void;
};

export type DropDownButtonProps = {
  children?: any;
  action?: (event: any) => void;
};

export type ClientCategoryManagementProps = {
  handleContentChange: () => void;
};

export type ClientGeneralInfoProps = {
  client: ClientModel | undefined;
  setClient: (client: ClientModel) => void;
  onSave?: () => void;
};

export type NewTaskButtonProps = {
  style?: string;
};

export type TaskProps = {
  advancedOptions?: boolean;
  name: string;
};

export type PropertyContainerProps = {
  property: string;
  propertyInput: any;
  propertyIcon?: any;
};

export type AlertsProps = {
  id: string;
  model: object;
  modelType: string;
  excludedFields: string[];
};

export type TaskPeriodicityProps = {
  getValues: UseFormGetValues<any>;
  watch: UseFormWatch<any>;
  control: Control<any, any>;
  setValue: UseFormSetValue<any>;
  setOpenPeriodicity: (value: boolean) => void;
  periodicities: any[];
};

export type ApprovalModalProps = {
  openModal: boolean;
  handleClose: () => void;
  handleApproveAction: () => void;
  message?: string;
};

export type SubtasksModalProps = {
  open: boolean;
  onClose: () => void;
  statuses: TaskStatusModel[] | [];
  parentTaskId: string | null;
  isParentFinished: boolean;
};

export type FileExplorerMinProps = {
  id?: any;
  name: string;
  deleteUrl?: string;
  shouldCRUDFile?: boolean;
  baseFolderPath?: string;
  files?: any[];
  componentTextDescription?: string;
};

export type AlertDrawerProps = {
  editAlert: AlertModel | null;
  open: boolean;
  propertyOptions: string[];
  ruleOptions: { label: string; value: string }[];
  addOrUpdateAlert: (alert: AlertModel) => void;
  onClose: () => void;
};

export type OneAlertProps = {
  propertyOptions: string[];
  ruleOptions: { label: string; value: string }[];
  onClose: () => void;
};

export type FilterConfiguratorProps = {
  model: any;
};

export type GeneralInfoTabProps = {
  users: UserModel[];
  instructors: InstructorModel[];
  products: any;
  allDocumentTypes: any[];
  managements: UserModel[];
  formData: any;
  saveOrder: any;
  getOrder: any;
  test?: any;
  triggerReload?: any;
  setTriggerReload?: any;
  accessPayments?: boolean;
  accessBooks?: boolean;
  clientDocuments?: any[];
  setClientDocuments?: (docs: any[]) => void;
  hasReadiness?: boolean;
  readinessProgress?: number;
  clientReadinessData?: any;
};

export type CustomizedStepperProps = {
  selectedStep: any;
  stepsArray: any[];
  onStepChange: (step: any) => void;
};

export type DocumentButtonProps = {
  documentType: string; // Could be 'psychological', 'medical', or 'criminal_record'
  buttonTitle: string; // Display name for the document button
  control: any;
  getValues: (string: string) => void;
  setValue: any;
  onDocumentSaved?: () => void;
  allDocumentTypes?: any[];
  existingDocument?: any;
};

export type RowItem = {
  label: string | JSX.Element | null;
  value: string | number | JSX.Element | null;
};

export type LicenceTypeProductInfoProps = {
  getValues: (string: string) => void;
  theme: any;
  setValue: (key: string, value: any) => void;
  control: any;
  emptyRow: RowItem[];
  instructors: InstructorModel[];
  vehicles: VehicleModel[];
  saveOrder: () => void;
  exam: any;
  retryExam: (exam: any) => void;
  clientDocuments?: any[];
  onDocumentSaved?: () => void;
  productData?: any;
  allDocumentTypes?: any[];
};

export type GenericModalButtonProps = {
  buttonTitle: string;
  modalTitle?: string;
  modalSubtitle?: any;
  saveAction?: any;
  content: any;
  buttonIcon: any;
  buttonColor?:
    | "primary"
    | "inherit"
    | "secondary"
    | "success"
    | "error"
    | "info"
    | "warning"
    | undefined;
  formId?: string;
  buttonSize?: "small" | "medium" | "large" | undefined;
  formRef?: any;
  triggerOnClose?: boolean;
};

export type IDocumentsManagerProps = {
  clientId?: string;
  orderId?: string;
  orderTypeId?: string;
  clientData?: any;
  productData?: any;
  clientDocuments?: any[];
  setClientDocuments?: (docs: any[]) => void;
  triggerReload?: any;
  setTriggerReload?: any;
  allStatuses?: any[];
  delete?: boolean;
  showGenerateDocumentButton?: boolean;
  showPrintButton?: boolean;
  hideDownloadButton?: boolean;
  hidePrintButton?: boolean;
  showManageTypesButton?: boolean;
};

export type IAddDocumentModalProps = {
  onClose: () => void;
  onSave?: (document: DocumentsModel) => void;
  formRef: React.RefObject<HTMLFormElement>;
  editData?: any;
  clientId?: string;
  orderId?: string;
  requiredDocumentTypes?: string[];
  existingDocuments?: any[];
  allStatuses?: any[];
  onStatusUpdated?: () => void;
  showManageTypesButton?: boolean;
};

export type IDocumentPreviewModalProps = {
  open: boolean;
  onClose: () => void;
  document: DocumentsModel;
};

export type DocumentValidityProps = {
  name?: string;
};

export type EditClassProps = {
  classes: ClassModel[];
  control: any;
};
export type EditCompanyProps = {
  companies: CompanyModel[];
  control: any;
};

export type HoursPanelProps = {
  instructors: InstructorModel[];
  nrOfDrivingHours: number;
  getOrder: any;
  setTriggerReload: any;
  triggerReload: undefined | boolean;
  nrOfSupplimentaryHours: number | string;
  instructorId: InstructorModel;
  addHours?: boolean;
  clientId?: string;
  vehicleId?: string;
  supplementaryHourPrice?: string | number;
  addSupplementaryHours?: boolean;
  isInstructor?: boolean;
};

export type HoursFormProps = {
  instructors: InstructorModel[];
  onClose: () => void;
  defaultValues?: any;
  showKm?: boolean;
  showVehicle?: boolean;
  products?: ProductsModel[];
  isInstructor?: boolean;
  restrictAccess?: boolean;
};

export type ClientHistoryProps = {
  getCurrentValues: any;
  products: any;
};

export type CollectPaymentFormProps = {
  setTriggerReload: any;
  collectPaymentType: string | string[];
  workPoint: WorkPointModel;
  clientId: string;
  onClose?: () => void;
  maxAmount?: number;
};

export type PaymentsListProps = {
  triggerReload: undefined | boolean;
};

export type SupplimentaryHoursProps = {
  paymentMethods?: string[];
  suplimentaryHour: any;
  setSupplimentaryHour: any;
  errors: any;
  supplementaryHourPrice?: string | number;
};

export type MessagesPanelProps = {
  triggerReload: undefined | boolean;
};

export type ARRNumberProps = {
  control: any;
  getValues: UseFormGetValues<typeof OrderValidationSchema>;
};

export type TriggerReloadProps = {
  triggerReload: undefined | boolean;
};

export type AccountsTableProps = {
  onClose: () => void;
  contextValue: AccountModel;
};
/* eslint-disable no-unused-vars */
export enum PaymentFormType {
  SALARY = "salary",
  PAYMENT = "payment",
}
/* eslint-enable no-unused-vars */
export type PaymentFormProps = {
  instructor?: InstructorModel;
  workPoint?: WorkPointModel;
  onClose: () => void;
  paymentType: PaymentFormType.PAYMENT | PaymentFormType.SALARY;
  clients?: ClientModel[];
  formRef?: any;
  showPaymentType?: boolean;
  officeAssistants?: UserModel[];
  paymentMethods?: string[];
  restrictByWorkPoint?: boolean;
  onSearchClients?: (search: string | null) => void;
};

export type InstructorPaymentsProps = TriggerReloadProps & {
  instructorId: string;
};

export type WithdrawMoneyFormProps = {
  onClose: () => void;
};

export type GroupedStatus = {
  name: string;
  _id: string[];
};

export type ExamHourProps = {
  instructor: InstructorModel;
  instructors: InstructorModel[];
};

export type GenericFormProps<contextModel = any> = {
  onClose: () => void;
  selectedRow: contextModel | undefined;
};
export type CourseFormProps = {
  selectedRow: CourseModel;
  open: boolean;
  onClose: () => void;
  products: ProductsModel[];
};

export type ModuleFormProps = {
  open?: boolean;
  courses: CourseModel[];
  onClose?: () => void;
  contextValue: any;
};

// Grouped exam sets type for questionnaire
export type GroupedExamSets = {
  [productName: string]: ExamsSetModel[];
};

export interface ExamQuestionFormProps {
  onClose: () => void;
  selectedRow?: ExamQuestionModel;
  examSets: {
    examCategory: any;
    _id: string;
    name: string;
  }[];
}

// Settings Management Types
export type Setting = {
  name: string;
  translation: string;
  value: string | number | boolean | object;
};

export type SettingsFormData = {
  [key: string]: string | number | boolean | object;
};

export interface IDocumentTypeManagerModalProps {
  open: boolean;
  onClose: () => void;
  documentTypes: any[];
  onRefresh: () => void;
}

export type SettingFieldProps = {
  setting: Setting;
  value: string | number | boolean | object;
  onChange: (name: string, value: string | number | boolean | object) => void;
};

export type SubscriptionFormProps = {
  onClose: () => void;
  selectedRow: SubscriptionModel | undefined;
  open: boolean;
};

export type IOnlineLesson = {
  _id: string;
  title: string;
  startDate: string;
  instructorId: any;
  productIds: any[];
  targetScope: import("./Enums").EOnlineLessonTargetScope;
  lessonUrl: string;
  seriesId?: string;
  createdBy?: any;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};
